import React, { createContext, useContext, useState, useCallback } from "react";

const EditorContext = createContext();

export const useEditor = () => {
    const context = useContext(EditorContext);
    if (!context) {
        throw new Error("useEditor must be used within an EditorProvider");
    }
    return context;
};

export const EditorProvider = ({ children }) => {
    const [openFiles, setOpenFiles] = useState([]);
    const [activeFileId, setActiveFileId] = useState(null);
    const [diffVersion, setDiffVersion] = useState(null); // { original: string, modified: string, version: string }
    const [fileStates, setFileStates] = useState({}); // { [fileId]: { code: string, hasChanges: boolean, originalCode: string, viewState: any } }
    const [prefetchedCode, setPrefetchedCode] = useState({}); // { [fileId]: string } - background-downloaded code for search

    const openFile = useCallback((file) => {
        setOpenFiles((prev) => {
            if (!prev.find((f) => f.id === file.id)) {
                return [...prev, file];
            }
            return prev;
        });
        setActiveFileId(file.id);
        // Clear diff view when switching files — diff is file-specific
        setDiffVersion(null);

        // Initialize file state — code may arrive later via loadFileCode
        setFileStates((prev) => {
            const existingState = prev[file.id];

            if (!existingState) {
                return {
                    ...prev,
                    [file.id]: {
                        code: file.code || "",
                        hasChanges: false,
                        originalCode: file.code || "",
                        isLoadingCode: !file.code,
                    },
                };
            }

            if (
                !existingState.hasChanges &&
                file.code &&
                existingState.originalCode !== file.code
            ) {
                return {
                    ...prev,
                    [file.id]: {
                        ...existingState,
                        code: file.code || "",
                        originalCode: file.code || "",
                        hasChanges: false,
                        isLoadingCode: false,
                    },
                };
            }

            return prev;
        });
    }, []);

    // Load code for a file after it's been opened (lazy loading)
    const loadFileCode = useCallback((fileId, code) => {
        setFileStates((prev) => {
            const existingState = prev[fileId];
            if (!existingState) return prev;

            // Don't overwrite unsaved user changes
            if (existingState.hasChanges) {
                return {
                    ...prev,
                    [fileId]: {
                        ...existingState,
                        originalCode: code || "",
                        isLoadingCode: false,
                    },
                };
            }

            return {
                ...prev,
                [fileId]: {
                    ...existingState,
                    code: code || "",
                    originalCode: code || "",
                    hasChanges: false,
                    isLoadingCode: false,
                },
            };
        });
    }, []);

    const closeFile = useCallback(
        (fileId) => {
            // Clear diff when closing the file that's being diffed
            if (activeFileId === fileId) {
                setDiffVersion(null);
            }

            // Single atomic update to avoid race conditions
            setOpenFiles((prev) => {
                const newOpenFiles = prev.filter((f) => f.id !== fileId);

                // Update active file if we're closing the currently active one
                if (activeFileId === fileId) {
                    if (newOpenFiles.length > 0) {
                        // Select the last opened file
                        setActiveFileId(
                            newOpenFiles[newOpenFiles.length - 1].id
                        );
                    } else {
                        setActiveFileId(null);
                    }
                }

                return newOpenFiles;
            });

            // Clean up file state when closing to prevent stale data
            // Only remove if there are no unsaved changes to avoid data loss
            setFileStates((prev) => {
                const fileState = prev[fileId];
                if (fileState && !fileState.hasChanges) {
                    const { [fileId]: removed, ...rest } = prev;
                    return rest;
                }
                return prev;
            });
        },
        [activeFileId]
    );

    const updateFileCode = useCallback((fileId, newCode) => {
        setFileStates((prev) => {
            const currentState = prev[fileId];
            if (!currentState) return prev;

            return {
                ...prev,
                [fileId]: {
                    ...currentState,
                    code: newCode,
                    hasChanges: newCode !== currentState.originalCode,
                },
            };
        });
    }, []);

    // Save editor view state (cursor position, scroll, undo history) for a file
    const saveViewState = useCallback((fileId, viewState) => {
        setFileStates((prev) => {
            const currentState = prev[fileId];
            if (!currentState) return prev;

            return {
                ...prev,
                [fileId]: {
                    ...currentState,
                    viewState,
                },
            };
        });
    }, []);

    // Get view state for a file
    const getViewState = useCallback(
        (fileId) => {
            return fileStates[fileId]?.viewState || null;
        },
        [fileStates]
    );

    // Called when the file is saved or when we receive an update from the server (and want to overwrite)
    const setOriginalCode = useCallback(
        (fileId, code, updateCurrent = false) => {
            setFileStates((prev) => {
                const currentState = prev[fileId];
                if (!currentState) return prev;

                const newCode = updateCurrent ? code : currentState.code;

                return {
                    ...prev,
                    [fileId]: {
                        ...currentState,
                        originalCode: code,
                        code: newCode,
                        hasChanges: newCode !== code,
                    },
                };
            });
            // Keep prefetch cache in sync so search and re-opens use latest code
            setPrefetchedCode((prev) => {
                if (prev[fileId] === code) return prev;
                return { ...prev, [fileId]: code };
            });
        },
        []
    );

    // Marks all open files as having no unsaved changes.
    // Use before a forced sync (e.g. after rollback) so syncComponents
    // will overwrite the editor code with the new backend data.
    const clearUnsavedChanges = useCallback(() => {
        setFileStates((prev) => {
            const next = {};
            let changed = false;
            for (const [id, state] of Object.entries(prev)) {
                if (state.hasChanges) {
                    next[id] = { ...state, hasChanges: false };
                    changed = true;
                } else {
                    next[id] = state;
                }
            }
            return changed ? next : prev;
        });
    }, []);

    // Store prefetched code for search without opening the file
    const prefetchCode = useCallback((fileId, code) => {
        setPrefetchedCode((prev) => {
            if (prev[fileId] === code) return prev;
            return { ...prev, [fileId]: code };
        });
    }, []);

    // Syncs the state with the latest components from the backend.
    // Pass force=true to overwrite unsaved changes (e.g. after rollback).
    // Components may not have code (lazy loaded), so only sync code if present.
    const syncComponents = useCallback((components, force = false) => {
        setFileStates((prev) => {
            const next = { ...prev };
            let hasUpdates = false;

            components.forEach((comp) => {
                if (next[comp.id]) {
                    // Only sync code if the component actually has code
                    // (components from list endpoint no longer include code)
                    if (comp.code != null && next[comp.id].originalCode !== comp.code) {
                        const keepUserCode = next[comp.id].hasChanges && !force;
                        const newCode = keepUserCode
                            ? next[comp.id].code
                            : comp.code || "";
                        next[comp.id] = {
                            ...next[comp.id],
                            originalCode: comp.code || "",
                            code: newCode,
                            hasChanges: newCode !== (comp.code || ""),
                        };
                        hasUpdates = true;
                    }
                }
            });

            return hasUpdates ? next : prev;
        });

        // Also update openFiles metadata (like name) if needed
        setOpenFiles((prev) => {
            let hasUpdates = false;
            const newFiles = prev.map((f) => {
                const updatedComp = components.find((c) => c.id === f.id);
                if (
                    updatedComp &&
                    (updatedComp.name !== f.name ||
                        updatedComp.filePath !== f.filePath)
                ) {
                    hasUpdates = true;
                    return updatedComp;
                }
                return f;
            });
            return hasUpdates ? newFiles : prev;
        });
    }, []);

    // Wrap setActiveFileId to also clear diff when switching files via tabs
    const switchActiveFile = useCallback((fileId) => {
        setActiveFileId((prev) => {
            if (prev !== fileId) {
                setDiffVersion(null);
            }
            return fileId;
        });
    }, []);

    const value = {
        openFiles,
        activeFileId,
        fileStates,
        prefetchedCode,
        diffVersion,
        setDiffVersion,
        openFile,
        closeFile,
        setActiveFileId: switchActiveFile,
        updateFileCode,
        setOriginalCode,
        loadFileCode,
        prefetchCode,
        syncComponents,
        clearUnsavedChanges,
        saveViewState,
        getViewState,
    };

    return (
        <EditorContext.Provider value={value}>
            {children}
        </EditorContext.Provider>
    );
};
